import React, { useState } from 'react';
import { Button, Box, Menu, MenuItem, CircularProgress } from '@mui/material';
import { TableChart as CsvIcon, Slideshow as SlideshowIcon, Description as DescriptionIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import { generateWordDocument, downloadWordDocument } from '../../utils/wordDocumentGenerator';

export default function ExportButtons({ seanceId, seance, asMenuItems = false, onClose: onCloseProp }) {
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPPTX, setIsExportingPPTX] = useState(false);
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onCloseProp) {
      onCloseProp();
    }
  };
  
  const handleExportExcelWithClose = async () => {
    await handleExportExcel();
    if (asMenuItems && onCloseProp) {
      onCloseProp();
    }
  };
  
  const handleExportPPTXWithClose = async () => {
    await handleExportPPTX();
    if (asMenuItems && onCloseProp) {
      onCloseProp();
    }
  };
  
  const handleExportWordWithClose = async () => {
    await handleExportWord();
    if (asMenuItems && onCloseProp) {
      onCloseProp();
    }
  };

  const handleExportExcel = async () => {
    if (!seanceId) {
      console.error('No seance ID provided');
      handleClose();
      return;
    }

    setIsExportingExcel(true);
    
    try {
      console.log('Exporting Excel for seance:', seanceId);
      
      // Fetch seance
      const response = await api.seance.getSeanceById(seanceId);
      const updatedSeance = response.data?.seance || response;
      console.log('Fetched seance');
      
      // Generate Excel workbook
      const workbook = generateExcelContent(updatedSeance);
      
      // Convert workbook to binary Excel file
      const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
      
      // Download the file
      downloadFile(excelBuffer, `${seanceId}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Erreur lors de l\'exportation. Veuillez réessayer.');
    } finally {
      setIsExportingExcel(false);
      handleClose();
    }
  };

  const handleExportPPTX = async () => {
    if (!seanceId) {
      console.error('No seance ID provided');
      handleClose();
      return;
    }

    setIsExportingPPTX(true);
    
    try {
      console.log('Exporting PPTX for seance:', seanceId);
      
      // Send POST request to generate PPTX
      const response = await api.post(`seances/${seanceId}/generate-pptx`, {});
      
      if (response.success && response.data) {
        const { pptx: pptBase64, filename } = response.data;
        
        // Decode base64 to binary
        const binaryString = atob(pptBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Download the file
        downloadFile(bytes, filename || `${seanceId}.pptx`, 'application/vnd.openxmlformats-officedocument.presentationml.presentation');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error exporting PPTX:', error);
      alert('Erreur lors de l\'exportation PPTX. Veuillez réessayer.');
    } finally {
      setIsExportingPPTX(false);
      handleClose();
    }
  };

  const handleExportWord = async () => {
    if (!seance) {
      alert('Aucune séance à exporter');
      handleClose();
      return;
    }

    setIsExportingWord(true);
    
    try {
      console.log('Generating Word document for seance:', seanceId);
      
      // Generate the Word document
      const blob = await generateWordDocument(seance);
      
      // Generate filename from seance title
      const filename = `${seance.title || 'seance'}_${seanceId}.docx`.replace(/[^a-z0-9.-_]/gi, '_');
      
      // Download the document
      await downloadWordDocument(blob, filename);
      
      console.log('Word document exported successfully');
    } catch (error) {
      console.error('Error exporting Word document:', error);
      alert('Erreur lors de l\'exportation du document Word. Veuillez vérifier que la bibliothèque docx est installée.');
    } finally {
      setIsExportingWord(false);
      handleClose();
    }
  };

  const generateExcelContent = (seance) => {
    if (!seance) {
      const ws = XLSX.utils.aoa_to_sheet([]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Quiz Questions');
      return wb;
    }
    
    const rows = [];
    
    // Find max number of answers across all quizzes and sondages (for column structure)
    let maxAnswers = 0;
    seance.sequences?.forEach((sequence) => {
      sequence.blocs?.forEach((block) => {
        if (block.type_bloc === 'quiz' || block.type_bloc === 'sondage') {
          const answers = block.bloc_data?.answers || [];
          maxAnswers = Math.max(maxAnswers, answers.length);
        }
      });
    });
    
    // If no quizzes, default to 3 answers to ensure column F exists
    if (maxAnswers === 0) maxAnswers = 3;
    
    // Helper function to create an image row
    const createImageRow = (imageUrl, imageTitle = '') => {
      const emptyAnswers = Array(maxAnswers).fill('');
      return [imageUrl, imageTitle || '', ...emptyAnswers, 'תמונה'];
    };
    
    // Browse all blocs and add rows
    seance.sequences?.forEach((sequence) => {
      sequence.blocs?.forEach((block) => {
        const blockType = block.type_bloc || 'unknown';
        
        // Handle quiz blocks
        if (blockType === 'quiz') {
          const question = block.bloc_data?.question || '';
          const answers = block.bloc_data?.answers || [];
          
          // Find correct answer column (1-indexed, starting from column C)
          let correctAnswerColumn = 0;
          const answerTexts = [];
          
          answers.forEach((answer, index) => {
            const answerText = answer.answer || answer.text || '';
            answerTexts.push(answerText);
            
            const isCorrect = answer.outcome === 'good answer' || answer.is_correct || false;
            if (isCorrect && correctAnswerColumn === 0) {
              correctAnswerColumn = index + 1;
            }
          });
          
          // Pad answers to maxAnswers
          while (answerTexts.length < maxAnswers) {
            answerTexts.push('');
          }
          
          // Add quiz row: [Question, Correct Answer Column, ...answers, Type]
          rows.push([question, correctAnswerColumn || '', ...answerTexts, 'שאלה']);
          
          // Add validated images from this quiz block
          const images = block.bloc_data?.images || [];
          images.forEach((image) => {
            if (image.status_seance === 'validated') {
              const imageUrl = image.compressed_url || image.img_url || image.url || '';
              const imageTitle = image.title || '';
              if (imageUrl) {
                rows.push(createImageRow(imageUrl, imageTitle));
              }
            }
          });
        }
        
        // Handle sondage blocks
        if (blockType === 'sondage') {
          const question = block.bloc_data?.question || '';
          const answers = block.bloc_data?.answers || [];
          
          const answerTexts = [];
          
          answers.forEach((answer) => {
            const answerText = answer.answer || answer.text || '';
            answerTexts.push(answerText);
          });
          
          // Pad answers to maxAnswers
          while (answerTexts.length < maxAnswers) {
            answerTexts.push('');
          }
          
          // Add sondage row: [Question, '', ...answers, 'סקר'] (empty second column)
          rows.push([question, '', ...answerTexts, 'סקר']);
          
          // Add validated images from this sondage block
          const images = block.bloc_data?.images || [];
          images.forEach((image) => {
            if (image.status_seance === 'validated') {
              const imageUrl = image.compressed_url || image.img_url || image.url || '';
              const imageTitle = image.title || '';
              if (imageUrl) {
                rows.push(createImageRow(imageUrl, imageTitle));
              }
            }
          });
        }
        
        // Handle image blocks - add validated images
        if (blockType === 'image') {
          const images = block.bloc_data?.images || [];
          images.forEach((image) => {
            if (image.status_seance === 'validated') {
              const imageUrl = image.compressed_url || image.img_url || image.url || '';
              const imageTitle = image.title || '';
              if (imageUrl) {
                rows.push(createImageRow(imageUrl, imageTitle));
              }
            }
          });
        }
        
        // Handle teacher_notes blocks - add validated images
        if (blockType === 'teacher_notes') {
          const images = block.bloc_data?.images || [];
          images.forEach((image) => {
            if (image.status_seance === 'validated') {
              const imageUrl = image.compressed_url || image.img_url || image.url || '';
              const imageTitle = image.title || '';
              if (imageUrl) {
                rows.push(createImageRow(imageUrl, imageTitle));
              }
            }
          });
        }
        
        // Handle music blocks
        if (blockType === 'music') {
          const musicUrl = block.bloc_data?.url || '';
          const musicName = block.bloc_data?.name || '';
          const emptyAnswers = Array(maxAnswers).fill('');
          // Add music row: [URL, Name, ...emptyAnswers, 'אודיו']
          rows.push([musicUrl, musicName, ...emptyAnswers, 'אודיו']);
        }
      });
    });
    
    // Create worksheet and workbook
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Quiz Questions');
    
    return wb;
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const menuItems = (
    <>
      <MenuItem 
        onClick={asMenuItems ? handleExportExcelWithClose : handleExportExcel}
        disabled={isExportingExcel}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: '150px'
        }}
      >
        {isExportingExcel ? (
          <CircularProgress size={16} />
        ) : (
          <CsvIcon sx={{ fontSize: '1.2rem', color: '#10b981' }} />
        )}
        {isExportingExcel ? 'Export...' : 'Excel'}
      </MenuItem>
      
      <MenuItem 
        onClick={asMenuItems ? handleExportPPTXWithClose : handleExportPPTX}
        disabled={isExportingPPTX}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: '150px'
        }}
      >
        {isExportingPPTX ? (
          <CircularProgress size={16} />
        ) : (
          <SlideshowIcon sx={{ fontSize: '1.2rem', color: '#f59e0b' }} />
        )}
        {isExportingPPTX ? 'Export...' : 'PowerPoint'}
      </MenuItem>
      
      <MenuItem 
        onClick={asMenuItems ? handleExportWordWithClose : handleExportWord}
        disabled={isExportingWord}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: '150px'
        }}
      >
        {isExportingWord ? (
          <CircularProgress size={16} />
        ) : (
          <DescriptionIcon sx={{ fontSize: '1.2rem', color: '#2563eb' }} />
        )}
        {isExportingWord ? 'Génération...' : 'Word'}
      </MenuItem>
    </>
  );

  if (asMenuItems) {
    return menuItems;
  }

  return (
    <Box>
      <Button
        onClick={handleClick}
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        size="small"
        sx={{
          borderColor: '#2563eb',
          color: '#2563eb',
          borderRadius: '20px',
          px: 2,
          py: 0.5,
          fontSize: '0.8rem',
          fontWeight: 'bold',
          backgroundColor: '#eff6ff',
          '&:hover': {
            borderColor: '#1d4ed8',
            backgroundColor: '#dbeafe',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
          }
        }}
      >
        Exports
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {menuItems}
      </Menu>
    </Box>
  );
}
